/**
 * Flutter-faithful form primitives for the patient Add/Edit screens.
 *
 * These mirror the Flutter building blocks rather than the generic shadcn
 * patterns so the React Personal Details tab matches the Flutter layout
 * pixel-for-intent, rendered with Signal DS tokens (no hardcoded hex):
 *
 *   - `FormSection`  → Flutter `FormView` (`form_view_widget.dart`):
 *       sentence-case 12px medium label ABOVE a white rounded card. `bare`
 *       drops the card chrome (used by "I AM ADDING" / "ID CARD" which
 *       render their content directly on the page background).
 *   - `FieldLabel`   → Flutter `defaultFieldLabelStyle` field label.
 *   - `PillPicker`   → Flutter `FieldType.SingleTagPicker` (gender, consent,
 *       importance, marital, residency, ID type, interpreter): rounded-full
 *       chips, grey-100 / brand-primary.
 *   - `PatientTypeCards` → Flutter "I AM ADDING" / "PATIENT IS" big
 *       MINOR / ADULT cards (160×48, icon chip, 2px brand ring when active).
 *   - `FIELD_INPUT_CLASS` → filled grey text-input shape matching Flutter
 *       `textFieldBGcolor`.
 *
 * Tokens used: bg-surface-card, bg-grey-100/200, text-brand-primary,
 * text-text-{primary,muted,hint}, ring-brand-primary, border-border-default,
 * rounded-{full,lg,xl}, status-error. All Signal DS Tailwind tokens.
 */

import type { ReactNode } from 'react';
import { Baby, User } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Shared text-input shape for the patient forms.
 *
 * Unified to the Order-Requests / `NewFilePopup` control style (Andrew
 * 2026-06-09): **white surface + visible border + rounded-md + brand-primary
 * focus ring** — the same shape every other Signal-styled input on the
 * upload popup / order-requests surface uses
 * (`new-file-popup.tsx`: `h-10 px-3 rounded-md border border-border-default
 * bg-surface-card … focus:ring-2 focus:ring-brand-primary`). Previously this
 * was a borderless grey-filled shape ("Flutter-faithful"); switched so the
 * patient + docs surfaces match the order-requests controls.
 *
 * Apply to `<Input className={FIELD_INPUT_CLASS}>` on the patient forms.
 */
export const FIELD_INPUT_CLASS =
  'h-10 px-3 rounded-md border border-border-default bg-surface-card text-text-primary placeholder:text-text-hint text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:border-brand-primary';

/** Field label, sentence case at medium weight (Flutter `defaultFieldLabelStyle`, minus the caps). */
export function FieldLabel({
  children,
  required,
  requiredTone = 'error',
  className,
}: {
  children: ReactNode;
  required?: boolean;
  /** The star: red while the field is empty, `inherit` (the label's grey) once it has a value. */
  requiredTone?: 'error' | 'inherit';
  className?: string;
}) {
  return (
    <Label
      className={cn(
        'text-[length:var(--type-meta-size)] font-medium text-text-muted',
        className,
      )}
    >
      {children}
      {required ? (
        <span className={cn('ml-1', requiredTone === 'error' && 'text-status-error')}>*</span>
      ) : null}
    </Label>
  );
}

/**
 * FormView card — sentence-case section label above a white rounded card.
 * `bare` renders the children directly on the page background (no card),
 * matching Flutter sections that aren't wrapped in a `FormView`
 * ("I AM ADDING", "ID CARD", "PATIENT STATUS", "PATIENT IS").
 */
export function FormSection({
  title,
  required,
  subtitle,
  bare = false,
  children,
  dataTour,
}: {
  title: string;
  required?: boolean;
  subtitle?: string;
  bare?: boolean;
  children: ReactNode;
  /** Optional `data-tour` anchor for the release-tour framework. */
  dataTour?: string;
}) {
  return (
    <section className="mx-8 my-6" data-tour={dataTour}>
      <h2 className="flex items-center text-xs font-medium text-text-primary">
        {title}
        {required ? <span className="ml-1 text-status-error">*</span> : null}
      </h2>
      {subtitle ? (
        <p className="mt-2 text-size-sm font-semibold text-text-muted">{subtitle}</p>
      ) : null}
      {bare ? (
        <div className="mt-3">{children}</div>
      ) : (
        <div className="mt-3 rounded-xl bg-surface-card p-6">{children}</div>
      )}
    </section>
  );
}

export interface PillOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Single-select pill chooser — Flutter `FieldType.SingleTagPicker`.
 * `allowUnselect` (default true) mirrors Flutter: tapping the active pill
 * clears it, EXCEPT for consent (`allowUnselect: false`).
 */
export function PillPicker<T extends string>({
  options,
  value,
  onChange,
  allowUnselect = true,
  disabled = false,
  'aria-invalid': ariaInvalid,
}: {
  options: ReadonlyArray<PillOption<T>>;
  value: T | null | undefined;
  onChange: (next: T | null) => void;
  allowUnselect?: boolean;
  disabled?: boolean;
  'aria-invalid'?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2" aria-invalid={ariaInvalid}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(active && allowUnselect ? null : opt.value)}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
              active
                ? 'bg-brand-primary text-white'
                : 'bg-grey-100 text-brand-primary hover:bg-grey-200',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * MINOR / ADULT selectable cards — Flutter "I AM ADDING" (add) and
 * "PATIENT IS" (edit). `value` is `isAdult`; selecting a card calls
 * `onChange(isAdult)`. Selected card gets a 2px brand ring (Flutter
 * `Border.all(color: enabledBtnBGColor, width: 2)`).
 */
export function PatientTypeCards({
  value,
  onChange,
  disabled = false,
}: {
  /** Current `isAdult`. */
  value: boolean;
  onChange: (isAdult: boolean) => void;
  disabled?: boolean;
}) {
  const cards = [
    { isAdult: false, title: 'Minor', sub: '0-18 years old', Icon: Baby },
    { isAdult: true, title: 'Adult', sub: '>18 years old', Icon: User },
  ] as const;

  return (
    <div className="flex flex-wrap gap-4">
      {cards.map((c) => {
        const active = value === c.isAdult;
        return (
          <button
            key={c.title}
            type="button"
            disabled={disabled}
            onClick={() => onChange(c.isAdult)}
            className={cn(
              'flex h-12 w-40 items-center justify-between rounded-md bg-surface-card px-3 text-left transition-shadow',
              active
                ? 'ring-2 ring-brand-primary'
                : 'ring-1 ring-border-default hover:ring-brand-primary/50',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <span className="flex flex-col">
              <span className="text-[length:var(--type-meta-size)] font-medium text-text-muted">
                {c.title}
              </span>
              <span className="text-[length:var(--type-body-size)] text-text-primary">{c.sub}</span>
            </span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-grey-100 text-brand-primary">
              <c.Icon className="h-4 w-4" />
            </span>
          </button>
        );
      })}
    </div>
  );
}
